import {
  useKeyboardShortcutsContext,
  formatShortcut,
  type ShortcutDefinition,
} from "../../contexts/KeyboardShortcutsContext";
import { CloseIcon } from "../icons";
import { Modal } from "./Modal";
import styles from "./KeyboardShortcutsModal.module.css";

interface ShortcutGroupProps {
  title: string;
  shortcuts: ShortcutDefinition[];
}

function ShortcutGroup({ title, shortcuts }: ShortcutGroupProps) {
  if (shortcuts.length === 0) return null;

  return (
    <div className={styles.group}>
      <h3 className={styles.groupTitle}>{title}</h3>
      <ul className={styles.shortcutList}>
        {shortcuts.map((shortcut, index) => (
          <li key={`${shortcut.key}-${index}`} className={styles.shortcutItem}>
            <span className={styles.description}>{shortcut.description}</span>
            <kbd className={styles.kbd}>{formatShortcut(shortcut)}</kbd>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function KeyboardShortcutsModal() {
  const { isHelpModalOpen, closeHelpModal, shortcuts, activeMode } = useKeyboardShortcutsContext();

  if (!isHelpModalOpen) return null;

  const globalShortcuts = shortcuts.filter((s) => s.category === "global");
  const editShortcuts = shortcuts.filter((s) => s.category === "edit");
  const outlineShortcuts = shortcuts.filter((s) => s.category === "outline");
  const streamShortcuts = shortcuts.filter((s) => s.category === "stream");

  const categoryTitles: Record<string, string> = {
    global: "Global",
    edit: "Edit Mode",
    outline: "Outline Mode",
    stream: "Stream Mode",
  };

  return (
    <Modal
      onClose={closeHelpModal}
      labelledBy="shortcuts-title"
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <header className={styles.header}>
        <h2 id="shortcuts-title" className={styles.title}>
          Keyboard Shortcuts
        </h2>
        <button
          className={styles.closeButton}
          onClick={closeHelpModal}
          aria-label="Close shortcuts modal"
        >
          <CloseIcon />
        </button>
      </header>

      <div className={styles.content}>
        <div className={styles.modeIndicator}>
          Current mode: <strong>{activeMode}</strong>
        </div>

        <div className={styles.columns}>
          <div className={styles.column}>
            <ShortcutGroup title={categoryTitles.global} shortcuts={globalShortcuts} />
            {streamShortcuts.length > 0 && (
              <ShortcutGroup title={categoryTitles.stream} shortcuts={streamShortcuts} />
            )}
          </div>
          <div className={styles.column}>
            <ShortcutGroup title={categoryTitles.edit} shortcuts={editShortcuts} />
            <ShortcutGroup title={categoryTitles.outline} shortcuts={outlineShortcuts} />
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <p className={styles.hint}>
          Press <kbd className={styles.kbdSmall}>Esc</kbd> to close
        </p>
      </footer>
    </Modal>
  );
}
